import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import { blogPosts } from '@/data/blogPosts';
import { blogArticles, getAllArticles, articleIndustries } from '@/data/blog';
import EnhancedPageTransition from '@/components/EnhancedPageTransition';
import { motion } from 'framer-motion';

const Blog = () => {
  const { lang } = useParams<{ lang: string }>();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [hoveredPostId, setHoveredPostId] = useState<number | null>(null);

  // Update SEO meta tags
  useEffect(() => {
    updateMetaTags({
      title: `${t.blog} | Future Level`,
      description: t.blogMetaDescription || 'Erfahren Sie mehr über KI-Videomarketing, Produktvideos und Social Media Strategien in unserem Blog.',
      keywords: "KI Video Marketing Blog, AI Videoproduktion Tipps, Video Content Strategie, Social Media Video Trends",
      canonicalUrl: getCanonicalUrl('blog', currentLanguage),
      language: currentLanguage,
      ogType: "website"
    });
  }, [currentLanguage, t]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Event listeners for video loading and errors
      const handleError = () => {
        console.error("Blog page background video error");
        setVideoError(true);
      };
      
      const handleCanPlay = () => {
        console.log("Blog page video can play now");
        setVideoLoaded(true);
        
        // Try to play the video
        try {
          video.play().catch(e => {
            console.warn("Auto-play prevented:", e);
            // We don't set error here as the video can still be displayed
          });
        } catch (e) {
          console.warn("Play attempt error:", e);
        }
      };

      video.addEventListener('error', handleError);
      video.addEventListener('canplay', handleCanPlay);
      
      // Force load the video
      video.load();
      
      return () => {
        video.removeEventListener('error', handleError);
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, []);

  // Add enhanced debugging logs
  console.log('Blog articles from blog index:', Object.keys(blogArticles));
  console.log('All articles from getAllArticles():', getAllArticles().map(article => article.slug));
  console.log('New article available:', blogArticles['ki-video-individuell-markenkonform'] ? 'Yes' : 'No');

  // Combine blog posts from both sources
  const allPosts = [
    // First include the full blog articles
    ...getAllArticles().map(article => {
      console.log('Processing article:', article.slug);
      return {
        slug: article.slug,
        date: article.date,
        image: article.image,
        title: {
          de: article.meta.de.title,
          en: article.meta.en.title,
          fr: article.meta.fr.title,
          it: article.meta.it.title,
        },
        excerpt: {
          de: article.meta.de.description,
          en: article.meta.en.description,
          fr: article.meta.fr.description,
          it: article.meta.it.description,
        },
        // Add industry information for filtering
        industry: Object.keys(articleIndustries).find(
          ind => articleIndustries[ind as keyof typeof articleIndustries].includes(article.slug)
        ) || "general"
      };
    }),
    // Then include regular blog posts
    ...blogPosts.map(post => ({
      ...post,
      industry: "general" // Default industry for old blog posts
    }))
  ];

  console.log('Combined posts count:', allPosts.length);
  console.log('Current language:', currentLanguage);

  // Sort posts by date (newest first)
  allPosts.sort((a, b) => {
    const dateA = new Date(a.date.split('.').reverse().join('-'));
    const dateB = new Date(b.date.split('.').reverse().join('-'));
    return dateB.getTime() - dateA.getTime();
  });

  // Filter posts based on active filter
  const filteredPosts = activeFilter === 'all' 
    ? allPosts 
    : allPosts.filter(post => post.industry === activeFilter);

  // Define industry categories with translations
  const industryCategories = [
    { 
      id: 'all', 
      name: {
        de: 'Alle Beiträge',
        en: 'All Posts',
        fr: 'Tous les Articles',
        it: 'Tutti gli Articoli'
      }
    },
    { 
      id: 'ecommerce', 
      name: {
        de: 'E-Commerce',
        en: 'E-Commerce',
        fr: 'E-Commerce',
        it: 'E-Commerce'
      }
    },
    { 
      id: 'healthcare', 
      name: {
        de: 'Gesundheitswesen',
        en: 'Healthcare',
        fr: 'Santé',
        it: 'Sanità'
      }
    },
    { 
      id: 'coaching', 
      name: {
        de: 'Coaching & Beratung',
        en: 'Coaching & Consulting',
        fr: 'Coaching & Conseil',
        it: 'Coaching & Consulenza'
      }
    },
    { 
      id: 'general', 
      name: {
        de: 'Allgemein',
        en: 'General',
        fr: 'Général',
        it: 'Generale'
      }
    }
  ];

  // Animation variants for micro-interactions
  const postVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    hover: { y: -8, transition: { duration: 0.3 } }
  };
  
  // Button animation variants
  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } }
  };

  return (
    <EnhancedPageTransition variant="fade" duration={0.4} staggerChildren>
      <div className="flex flex-col min-h-screen bg-[#121212]">
        <Header />
        <main className="flex-grow py-16 md:py-24 relative">
          {/* Video background with improved implementation */}
          <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]">
            {!videoError ? (
              <video 
                ref={videoRef}
                className="h-full w-full object-cover opacity-0 transition-opacity duration-700"
                style={{ opacity: videoLoaded ? 0.15 : 0 }} 
                autoPlay 
                muted 
                loop 
                playsInline
                onError={() => setVideoError(true)}
                preload="metadata"
              >
                <source 
                  src="https://vdl.prod.dploy.me/veed-transcoder-outputs/cfd6b3af-164e-4c60-bdb1-32c25fdb9fb7/file.mp4" 
                  type="video/mp4"
                />
                {/* Fallback source if first one fails */}
                <source 
                  src="https://res.cloudinary.com/dndcmrxix/video/upload/v1744503284/veed-blog-background_eswq0q.mp4" 
                  type="video/mp4"
                />
              </video>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]"></div>
            )}
            
            {/* Neural pattern overlay like homepage */}
            <div className="absolute inset-0 neural-pattern z-0 opacity-10"></div>
            
            {/* Subtle overlay for readability */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-0"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400">
                {t.blogPageTitle}
              </h1>
              <p className="text-lg text-[#D6BCFA] max-w-3xl mx-auto">
                {t.blogPageSubtitle}
              </p>
            </motion.div>
            
            {/* Category filter buttons - Enhanced with micro-interactions */}
            <motion.div 
              className="flex flex-wrap justify-center gap-3 mb-12 px-2 sm:px-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {industryCategories.map((category, index) => (
                <motion.button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilter === category.id 
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30' 
                      : 'bg-[#2D3748]/50 text-gray-300 hover:bg-[#2D3748] hover:text-white'
                  }`}
                  whileHover={buttonVariants.hover}
                  whileTap={buttonVariants.tap}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + (index * 0.05), duration: 0.4 }}
                >
                  {category.name[currentLanguage as keyof typeof category.name]}
                </motion.button>
              ))}
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article 
                  key={index} 
                  className="glass-card rounded-xl overflow-hidden shadow-lg transition-all duration-300"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={postVariants}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  whileHover="hover"
                  onHoverStart={() => setHoveredPostId(index)}
                  onHoverEnd={() => setHoveredPostId(null)}
                >
                  <Link to={`/${currentLanguage}/blog/${post.slug}`} className="block overflow-hidden">
                    <div className="relative overflow-hidden h-56">
                      <motion.img 
                        src={post.image} 
                        alt={post.title[currentLanguage]} 
                        className="w-full h-full object-cover"
                        animate={{ 
                          scale: hoveredPostId === index ? 1.05 : 1,
                        }}
                        transition={{ duration: 0.4 }}
                        loading="lazy"
                        width="400"
                        height="225"
                      />
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"
                        animate={{ opacity: hoveredPostId === index ? 0.85 : 0.7 }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </Link>
                  <div className="p-5 md:p-6">
                    <span className="text-sm text-purple-300 mb-2 block">{post.date}</span>
                    <h2 className="text-xl font-semibold mb-3">
                      <Link to={`/${currentLanguage}/blog/${post.slug}`} className="text-white hover:text-purple-300 transition-colors">
                        {post.title[currentLanguage]}
                      </Link>
                    </h2>
                    <p className="text-gray-300 mb-4 line-clamp-3">
                      {post.excerpt[currentLanguage]}
                    </p>
                    <div className="flex items-center justify-between">
                      <Link 
                        to={`/${currentLanguage}/blog/${post.slug}`} 
                        className="text-purple-400 hover:text-purple-300 font-medium flex items-center group"
                      >
                        {t.readMore}
                        <motion.svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor" 
                          className="w-4 h-4 ml-1"
                          animate={{ x: hoveredPostId === index ? 5 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M14 5l7 7m0 0l-7 7m7-7H3" 
                          />
                        </motion.svg>
                      </Link>
                      
                      {post.industry && post.industry !== 'general' && (
                        <span className="text-xs px-2 py-1 rounded bg-purple-900/30 text-purple-300">
                          {industryCategories.find(cat => cat.id === post.industry)?.name[currentLanguage] || post.industry}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
            
            {filteredPosts.length === 0 && (
              <motion.div 
                className="text-center py-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-gray-400">
                  {currentLanguage === 'de' ? 'Keine Artikel in dieser Kategorie gefunden.' :
                   currentLanguage === 'fr' ? 'Aucun article trouvé dans cette catégorie.' :
                   currentLanguage === 'it' ? 'Nessun articolo trovato in questa categoria.' :
                   'No articles found in this category.'}
                </p>
              </motion.div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </EnhancedPageTransition>
  );
};

export default Blog;
